/**
 * PWA Features Hooks
 *
 * React hooks for PWA enhanced features
 */

import { useState, useEffect, useCallback } from 'react';
import { badging, webShare, wakeLock, checkPWACapabilities, PWACapabilities } from '../utils/pwaFeatures';

/**
 * Hook for Badging API
 * Manages app icon badge count
 */

export function useBadging() {
  const [badgeCount, setBadgeCount] = useState(0);
  const [isSupported] = useState(badging.isSupported());

  const updateBadge = useCallback(async (count: number) => {
    await badging.set(count);
    setBadgeCount(count);
  }, []);

  const clearBadge = useCallback(async () => {
    await badging.clear();
    setBadgeCount(0);
  }, []);

  const incrementBadge = useCallback(async () => {
    const newCount = badgeCount + 1;
    await badging.set(newCount);
    setBadgeCount(newCount);
  }, [badgeCount]);

  const decrementBadge = useCallback(async () => {
    const newCount = Math.max(0, badgeCount - 1);
    await badging.set(newCount);
    setBadgeCount(newCount);
  }, [badgeCount]);

  return {
    badgeCount,
    isSupported,
    updateBadge,
    clearBadge,
    incrementBadge,
    decrementBadge,
  };
}

/**
 * Hook for Web Share API
 * Provides sharing functionality with automatic fallback
 */

interface ShareOptions {
  title?: string;
  text?: string;
  url?: string;
  files?: File[];
}

export function useWebShare() {
  const [isSupported] = useState(webShare.isSupported());
  const [canShareFiles] = useState(webShare.canShareFiles());
  const [isSharing, setIsSharing] = useState(false);

  const share = useCallback(async (options: ShareOptions): Promise<boolean> => {
    setIsSharing(true);
    try {
      const result = await webShare.share(options);
      return result;
    } finally {
      setIsSharing(false);
    }
  }, []);

  const shareUrl = useCallback(async (url: string, title?: string, text?: string): Promise<boolean> => {
    return share({ url, title, text });
  }, [share]);

  const shareText = useCallback(async (text: string, title?: string): Promise<boolean> => {
    return share({ text, title });
  }, [share]);

  const shareFiles = useCallback(async (files: File[], title?: string, text?: string): Promise<boolean> => {
    return share({ files, title, text });
  }, [share]);

  const shareOrCopy = useCallback(async (options: ShareOptions): Promise<boolean> => {
    setIsSharing(true);
    try {
      return await webShare.shareOrCopy(options);
    } finally {
      setIsSharing(false);
    }
  }, []);

  return {
    isSupported,
    canShareFiles,
    isSharing,
    share,
    shareUrl,
    shareText,
    shareFiles,
    shareOrCopy,
  };
}

/**
 * Hook for Wake Lock API
 * Prevents screen from sleeping
 */

export function useWakeLock() {
  const [isSupported] = useState(wakeLock.isSupported());
  const [isActive, setIsActive] = useState(false);

  const request = useCallback(async () => {
    const success = await wakeLock.request();
    setIsActive(success);
    return success;
  }, []);

  const release = useCallback(async () => {
    await wakeLock.release();
    setIsActive(false);
  }, []);

  // Auto-release on unmount
  useEffect(() => {
    return () => {
      if (isActive) {
        wakeLock.release();
      }
    };
  }, [isActive]);

  return {
    isSupported,
    isActive,
    request,
    release,
  };
}

/**
 * Hook for checking all PWA capabilities
 * Useful for feature detection and progressive enhancement
 */

export function usePWACapabilities() {
  const [capabilities, setCapabilities] = useState<PWACapabilities>(() => checkPWACapabilities());

  useEffect(() => {
    // Recheck capabilities when visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setCapabilities(checkPWACapabilities());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return capabilities;
}
